import React, { Component } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { colors, distances, globalColumnMaxWidth, globalPageMaxWidth } from '../../../styles/constants';
import Page, { Back } from '../../../components/Page';
import Card from '../../../components/Card';
import { LoadingOverlay } from '../../../components/Loading';
import { H1 } from '../../../components/Typography';
import { OrderWithEvents, Order } from '../../../types/orders';

import NotFound from '../../../components/NotFound';
import OrderId from './OrderId';
import Items from '../../common/Items';
import Customer from '../../common/Customer';
import OrderHistory from './OrderHistory';
import Itinerary from '../../common/Itinerary';
import ExternalLinks from '../../common/ExternalLinks';
import Payments from './Payments';
import Metadata from '../../common/Metadata';
import Comments from './Comments';
import { AccountAdminUser } from '../../../types/management-auth';
import { withScopes, isAllowed } from '../../../auth/accessToken/Allow';

interface MatchParams {
    accountId: string;
    orderId: string;
}

export interface OrderPageProps extends WithTranslation, RouteComponentProps<MatchParams> {
    getOrder: (accountId: string, id: string) => void;
    createComment: (accountId: string, orderId: string, createCommentRequest: { body: string }) => void;
    deleteComment: (accountId: string, orderId: string, commentId: string) => void;
    openAuthorize: (order: Order) => void;
    openCapture: (order: Order) => void;
    openRefund: (order: Order) => void;
    openCancel: (order: Order) => void;
    order: OrderWithEvents | undefined;
    isLoading: boolean;
    createCommentIsLoading: boolean;
    isNotFound: boolean;
    getUsers: (accountId: string) => void;
    users: AccountAdminUser[];
    scopes: string[];
}

class OrderPage extends Component<OrderPageProps> {
    componentDidMount() {
        this.props.getOrder(this.props.match.params.accountId, this.props.match.params.orderId);
        if (isAllowed([['admin'], ['admin:admin'], ['admin:accounts'], ['read:accounts']], this.props.scopes)) {
            // only get users if allowed to
            this.props.getUsers(this.props.match.params.accountId);
        }
    }

    componentDidUpdate(prevProps: Readonly<OrderPageProps>, prevState: Readonly<{}>, snapshot?: any): void {
        if (this.props.match.params.orderId !== prevProps.match.params.orderId) {
            this.props.getOrder(this.props.match.params.accountId, this.props.match.params.orderId);
        }
    }

    render() {
        const {
            t,
            order,
            createCommentIsLoading,
            createComment,
            deleteComment,
            match,
            isNotFound,
            isLoading,
            openAuthorize,
            openCapture,
            openRefund,
            openCancel,
            users,
        } = this.props;
        const hasCustomer = order?.customer && Object.keys(order.customer).length > 0;
        const hasMetadata = order?.metadata && Object.keys(order.metadata).length > 0;
        return (
            <Page
                title={
                    <React.Fragment>
                        <Back />
                        <Title>{t('orders.order.title')}</Title>
                    </React.Fragment>
                }
                hasEnvFrame={true}
            >
                {order && (
                    <>
                        <OrderId
                            order={order}
                            openAuthorize={openAuthorize}
                            openCapture={openCapture}
                            openRefund={openRefund}
                            openCancel={openCancel}
                        />
                        <Items draftOrOrder={order} />
                        <ColumnsWrapper>
                            <Column>
                                {order.itinerary && order.itinerary.length && <Itinerary itinerary={order.itinerary} />}
                                <OrderHistory order={order} users={users}/>
                                {order.external_links && <ExternalLinks draftOrOrder={order} accountId={this.props.match.params.accountId} />}
                            </Column>
                            <Column>
                                {hasCustomer && <Customer draftOrOrder={order} />}
                                {order.deleted_at === undefined && order.closed_at === undefined && order.comments && (
                                    <Comments
                                        order={order}
                                        accountId={this.props.match.params.accountId}
                                        createComment={createComment}
                                        deleteComment={deleteComment}
                                        createCommentIsLoading={createCommentIsLoading}
                                        users={users}
                                    />
                                )}
                                {order.closed_at === undefined && (<Payments order={order} />)}
                                {hasMetadata && <Metadata draftOrOrder={order} />}
                            </Column>
                        </ColumnsWrapper>
                        {/* <br />
                        <Card>
                            <Pre>{JSON.stringify(order, null, 2)}</Pre>
                        </Card> */}
                    </>
                )}
                {!order && (
                    <Card title={undefined} noPadding>
                        <Wrapper>
                            {isNotFound && <NotFound id={match.params.orderId} title="orders.order.title" message="orders.order.not_found.message" callToAction="orders.order.not_found.call_to_action" link="../orders"  />}
                            {isLoading && <LoadingOverlay />}
                        </Wrapper>
                    </Card>
                )}
            </Page>
        );
    }
}


const ColumnsWrapper = styled.div`
    display: flex;
    position: relative;
    width: 100%;

    @media (max-width: ${globalColumnMaxWidth}px) {
        flex-direction: column;
    }
`;

const Column = styled.div`
    width: 50%;
    display: flex;
    flex-direction: column;
    min-height: 100px;

    @media (min-width: 1201px) {
        &:first-child {
            margin-right: ${distances.small};
        }
        &:last-child {
            margin-left: ${distances.small};
        }
    }

    @media (max-width: ${globalColumnMaxWidth}px) {
        width: 100%;
    }
`;
const Wrapper = styled.div`
    position: relative;
    min-height: calc(${distances.ginormous} + ${distances.large});
`;

const Title = styled(H1)`
    color: ${colors.text};
    display: inline-block;
        /* Heading/H6/Medium/Desktop */
        font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 28px; /* 140% */
    letter-spacing: -0.4px;
`;


// const Pre = styled.pre`
//     white-space: pre-wrap;
//     background: ${colors.backgroundAlt};
//     margin: 0;
//     padding: ${distances.normal};
//     overflow: scroll;
// `;

export default withScopes(withTranslation()(OrderPage));
